import { AxiosPromise } from 'axios';

import {
  DlpExpression,
  DlpExpressionAnalysis,
  DlpExpressionDetail,
  DlpKeyword,
  DlpKeywordDetail,
  DlpKeywordDictionaryKeyword,
  DlpTemplate,
  DlpTemplateDetail,
  OverAllPolicyType,
  PolicyItem,
  ServiceCollection,
  SkippedUsersInfo,
  TmDlpExpressionValidator,
} from '../pages/Policy/utils/types';
import request from '../utils/request';
import {
  ManualScanCancelAndRescanInfo,
  ManualScanInfo,
} from '@Policy/OverAllPolicyList/Modal';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import {
  AdvancedFeatureTypeEnum,
  AdvancedFeatureInfo,
  PolicyType,
  Service,
} from '@Types/app';

function apiGetPolicies(
  overAllPolicyType: OverAllPolicyType,
  collection: ServiceCollection,
  orgId: string
) {
  if (overAllPolicyType === OverAllPolicyType.ATP) {
    return getAtpPolicies(collection, orgId);
  } else {
    return getDlpPolicies(collection, orgId);
  }
}

function getAtpPolicies(
  collection: ServiceCollection,
  orgId: string
): AxiosPromise<PolicyItem[]> {
  return request({
    url: `/policies/${collection}/atp`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function getDlpPolicies(
  collection: ServiceCollection,
  orgId: string
): AxiosPromise<PolicyItem[]> {
  return request({
    url: `/policies/${collection}/dlp`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function getPolicyById(policyId: string) {
  return request({
    url: `/policies/${policyId}`,
    method: 'get',
  });
}

function updatePolicy(params: string, policyId: string) {
  return request({
    url: `/policies/${policyId}`,
    method: 'put',
    data: params,
  });
}

function createPolicy(params: string) {
  return request({
    url: `/policies`,
    method: 'post',
    data: params,
  });
}

function apiDeletePolicy(policyId: string) {
  return request({
    url: `/policies/${policyId}`,
    method: 'delete',
  });
}

function apiUpdatePolicyStatus(
  policyId: string,
  policyRealtimeEnabled: number
) {
  return request({
    url: `/policies/${policyId}/realtimeEnabledStatus`,
    method: 'post',
    data: {
      policyRealtimeEnabled: 1 - policyRealtimeEnabled, // 0 or 1
    },
  });
}

function apiUpdatePolicyPriority(policyId: string, priority: number) {
  return request({
    url: `/policies/${policyId}/reorder`,
    method: 'post',
    data: {
      position: priority,
    },
  });
}

function apiDuplicatePolicy(policyId: string) {
  return request({
    url: `/policies/${policyId}/duplicate`,
    method: 'post',
  });
}

function getPolicyTargetCount(
  policyType: number,
  policyId: string
): AxiosPromise<{
  targetCount: number;
  throttlingNumber: number;
}> {
  return request({
    url: `/manualScan/tasks/targetCount?policyId=${policyId}&policyType=${policyType}`,
    method: 'get',
  });
}

function registerTimeOfClick(): AxiosPromise<{
  state: number;
  message: string;
}> {
  return request({
    url: `/globalSettings/clickTimeProtection/registration`,
    method: 'post',
  });
}

function syncTargetUsersByServiceOnDemand(
  service: Service
): AxiosPromise<any[]> {
  return request({
    url: `/services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: 'onDemand',
      targetType: 'user',
    },
  });
}

function syncTargetObjectsByServiceOnDemand(
  service: Service
): AxiosPromise<any[]> {
  return request({
    url: `/services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: 'onDemand',
      targetType: 'object',
    },
  });
}

function syncTargetCommunitiesByServiceOnDemand(
  service: Service
): AxiosPromise<any[]> {
  return request({
    url: `/services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: 'onDemand',
      targetType: 'community',
    },
  });
}

function syncTargetProfilesByServiceOnDemand(
  service: Service
): AxiosPromise<any[]> {
  return request({
    url: `/services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: 'onDemand',
      targetType: 'profile',
    },
  });
}

function syncTargetDomainsByServiceOnDemand(
  service: Service
): AxiosPromise<any[]> {
  return request({
    url: `/services/${service}/targets/sync`,
    method: 'post',
    data: {
      syncMode: 'onDemand',
      targetType: 'domain',
    },
  });
}

/***********************************************************************************************************************
 * Dlp template apis
 ***********************************************************************************************************************
 */
function getDlpTemplates(language: string): AxiosPromise<DlpTemplate[]> {
  return request({
    url: `/policySettings/dlp/templates`,
    method: 'get',
    params: {
      language,
    },
  });
}

function getDlpTemplateById(
  templateId: string,
  language: string
): AxiosPromise<DlpTemplateDetail> {
  return request({
    url: `/policySettings/dlp/templates/${templateId}`,
    method: 'get',
    params: { language },
  });
}

function addDlpTemplate(template: DlpTemplateDetail) {
  return request({
    url: `/policySettings/dlp/templates`,
    method: 'post',
    data: template,
  });
}

function updateDlpTemplateById(
  templateId: string,
  template: DlpTemplateDetail
) {
  return request({
    url: `/policySettings/dlp/templates/${templateId}`,
    method: 'put',
    data: template,
  });
}

function deleteDlpTemplateById(templateId: string) {
  return request({
    url: `/policySettings/dlp/templates/${templateId}`,
    method: 'DELETE',
  });
}

function duplicateDlpTemplateById(
  templateId: string,
  templateName: string,
  language: string
) {
  return request({
    url: `/policySettings/dlp/templates/${templateId}/duplicate`,
    method: 'post',
    data: {
      templateName,
    },
    params: { language },
  });
}

function downloadDlpTemplate(ids: string[]) {
  return request({
    url: `/policySettings/dlp/templates/export`,
    method: 'post',
    data: { ids },
    timeout: 5 * 60 * 1000,
    responseType: 'blob',
  });
}

function importDlpEntity(formData: FormData) {
  return request({
    url: `/policySettings/dlpEntities/import`,
    method: 'post',
    data: formData,
    timeout: 5 * 60 * 1000,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

/***********************************************************************************************************************
 * Dlp expression apis
 ***********************************************************************************************************************
 */
function getDlpExpressions(language: string): AxiosPromise<DlpExpression[]> {
  return request({
    url: `/policySettings/dlp/expressions`,
    method: 'get',
    params: {
      language,
    },
  });
}

function getDlpExpressionById(
  expressionId: string,
  language: string
): AxiosPromise<DlpExpressionDetail> {
  return request({
    url: `/policySettings/dlp/expressions/${expressionId}`,
    method: 'get',
    params: { language },
  });
}

function addDlpExpression(expression: DlpExpressionDetail) {
  return request({
    url: `/policySettings/dlp/expressions`,
    method: 'post',
    data: expression,
  });
}

function updateDlpExpressionById(
  expressionId: string,
  expression: DlpExpressionDetail
) {
  return request({
    url: `/policySettings/dlp/expressions/${expressionId}`,
    method: 'put',
    data: expression,
  });
}

function deleteDlpExpressionById(expressionId: string) {
  return request({
    url: `/policySettings/dlp/expressions/${expressionId}`,
    method: 'DELETE',
  });
}

function duplicateDlpExpressionById(
  expressionId: string,
  expressionName: string,
  language: string
) {
  return request({
    url: `/policySettings/dlp/expressions/${expressionId}/duplicate`,
    method: 'post',
    data: {
      expressionName,
    },
    params: { language },
  });
}

function testExpression(data: {
  expression: any;
  testData: string;
}): AxiosPromise<{
  result: { len: number; pos: number }[] | null;
  testContent: string;
}> {
  return request({
    url: `/policySettings/dlp/expressions/test`,
    method: 'post',
    data,
  });
}

function analysisExpression(expression: DlpExpressionAnalysis): AxiosPromise<{
  scanCostTimeInMilliseconds: number;
}> {
  return request({
    url: `/policySettings/dlp/expressions/analysis`,
    method: 'post',
    data: expression,
  });
}

function downloadDlpExpression(ids: string[]) {
  return request({
    url: `/policySettings/dlp/expressions/export`,
    method: 'post',
    data: { ids },
    timeout: 5 * 60 * 1000,
    responseType: 'blob',
  });
}

/***********************************************************************************************************************
 * Dlp Keywords apis
 ***********************************************************************************************************************
 */
function getDlpKeywords(language: string): AxiosPromise<DlpKeyword[]> {
  return request({
    url: `/policySettings/dlp/keywords`,
    method: 'get',
    params: { language },
  });
}

interface DlpKeywordDetailKeywordOptional extends DlpKeywordDetail {
  dictionary: {
    keyword?: DlpKeywordDictionaryKeyword[];
    [key: string]: any;
  };
}
function getDlpKeywordById(
  keywordId: string,
  language: string
): AxiosPromise<DlpKeywordDetailKeywordOptional> {
  return request({
    url: `/policySettings/dlp/keywords/${keywordId}`,
    method: 'get',
    params: { language },
  });
}

function addDlpKeyword(keyword: DlpKeywordDetail) {
  return request({
    url: `/policySettings/dlp/keywords`,
    method: 'post',
    data: keyword,
  });
}

function updateDlpKeywordById(keywordId: string, keyword: DlpKeywordDetail) {
  return request({
    url: `/policySettings/dlp/keywords/${keywordId}`,
    method: 'put',
    data: keyword,
  });
}

function deleteDlpKeywordById(keywordId: string) {
  return request({
    url: `/policySettings/dlp/keywords/${keywordId}`,
    method: 'DELETE',
  });
}

function duplicateDlpKeywordById(
  keywordId: string,
  keywordName: string,
  language: string
) {
  return request({
    url: `/policySettings/dlp/keywords/${keywordId}/duplicate`,
    method: 'post',
    data: {
      keywordName,
    },
    params: { language },
  });
}

function downloadDlpKeyword(ids: string[]) {
  return request({
    url: `/policySettings/dlp/keywords/export`,
    method: 'post',
    data: { ids },
    timeout: 5 * 60 * 1000,
    responseType: 'blob',
  });
}

//这个接口可以缓存
function getDlpValidatorsList(
  language: string
): AxiosPromise<TmDlpExpressionValidator[]> {
  return request({
    url: `/policySettings/dlp/validators`,
    method: 'get',
    params: { language },
  });
}

function compileScanHistory(parentTaskId: string, policyId: string) {
  return request({
    url: `/report/scanHistories/${parentTaskId}/${policyId}`,
    method: 'get',
  });
}

function runManualScan(manualScanInfo: ManualScanInfo) {
  return request({
    url: `/manualScan/tasks`,
    method: 'post',
    data: manualScanInfo,
  });
}

function cancelManualScan(
  taskId: number,
  manualScanCancelInfo: ManualScanCancelAndRescanInfo
) {
  return request({
    url: `/manualScan/tasks/${taskId}/cancel`,
    method: 'post',
    data: manualScanCancelInfo,
  });
}

function rescanManualScan(
  taskId: number,
  manualScanRescanInfo: ManualScanCancelAndRescanInfo
) {
  return request({
    url: `/manualScan/tasks/${taskId}/rescan`,
    method: 'post',
    data: manualScanRescanInfo,
  });
}

function getFailedUserList(
  taskId: string,
  policyType: PolicyTypeEnum
): AxiosPromise<string[]> {
  return request({
    url: `/manualScan/tasks/${taskId}/failedUsers`,
    method: 'post',
    data: { policyType },
  });
}

function getSkippedUsersList(
  taskId: string,
  policyType: PolicyTypeEnum
): AxiosPromise<SkippedUsersInfo> {
  return request({
    url: `/manualScan/tasks/${taskId}/skippedUsers?policyType=${policyType}`,
    method: 'get',
  });
}

function getAdvancedFeatureInfo(
  action: AdvancedFeatureTypeEnum,
  policyType: PolicyTypeEnum
): AxiosPromise<AdvancedFeatureInfo> {
  return request({
    url: `/policies/credit/getAdvanceFeatureInfo`,
    method: 'get',
    params: { action, policyType },
  });
}

function apiSearchPolices(
  companyGuid: string,
  filter: {
    policyName?: string;
    policyType?: PolicyType;
  }
): AxiosPromise<PolicyItem[]> {
  return request({
    url: `/policies/search`,
    method: 'post',
    data: { companyGuid, filter },
  });
}

export default {
  apiGetPolicies,
  getPolicyById,
  updatePolicy,
  createPolicy,
  apiDeletePolicy,
  apiUpdatePolicyStatus,
  apiUpdatePolicyPriority,
  apiDuplicatePolicy,
  getPolicyTargetCount,
  registerTimeOfClick,
  syncTargetUsersByServiceOnDemand,
  syncTargetObjectsByServiceOnDemand,
  syncTargetCommunitiesByServiceOnDemand,
  syncTargetProfilesByServiceOnDemand,
  syncTargetDomainsByServiceOnDemand,
  getDlpTemplates,
  getDlpTemplateById,
  addDlpTemplate,
  updateDlpTemplateById,
  deleteDlpTemplateById,
  duplicateDlpTemplateById,
  downloadDlpTemplate,
  importDlpEntity,
  getDlpExpressions,
  getDlpExpressionById,
  addDlpExpression,
  updateDlpExpressionById,
  deleteDlpExpressionById,
  duplicateDlpExpressionById,
  testExpression,
  analysisExpression,
  downloadDlpExpression,
  getDlpKeywords,
  getDlpKeywordById,
  addDlpKeyword,
  updateDlpKeywordById,
  deleteDlpKeywordById,
  duplicateDlpKeywordById,
  downloadDlpKeyword,
  getDlpValidatorsList,
  compileScanHistory,
  runManualScan,
  cancelManualScan,
  rescanManualScan,
  getFailedUserList,
  getSkippedUsersList,
  getAdvancedFeatureInfo,
  apiSearchPolices,
};
